import { Transaction, TransactionObjectArgument } from '@mysten/sui/transactions';

export type MoveNumber = bigint | string | number;
export type Ref<T> = T | ResultRef;
export type ObjectId = string;

export class MoveObject {
  constructor(public readonly object: string) {}

  moveArg(txb: Transaction) {
    return txb.object(this.object);
  }
}

export class ObjectVector {
  constructor(public readonly objects: string[]) {}

  moveArgs(txb: Transaction) {
    return txb.makeMoveVec({ elements: this.objects.map((o) => txb.object(o)) });
  }
}

export class ResultRef {
  constructor(public readonly result: TransactionObjectArgument) {
    // eslint-disable-next-line no-constructor-return
    return new Proxy(this, {
      set() {
        throw new Error('The transaction result is a proxy, and does not support setting properties directly');
      },
      get(target, property) {
        // This allows this transaction argument to be used in the singular form:
        if (property in target) {
          return Reflect.get(target, property);
        }

        const nestedResultFor = (resultIndex: number) =>
          new ResultRef((target.result as unknown as TransactionObjectArgument[])[resultIndex]);

        // Support destructuring:
        if (property === Symbol.iterator) {
          // eslint-disable-next-line func-names
          return function* () {
            let i = 0;
            while (true) {
              yield nestedResultFor(i);
              i++;
            }
          };
        }

        if (typeof property === 'symbol') {
          // eslint-disable-next-line consistent-return
          return;
        }

        const resultIndex = parseInt(property, 10);
        if (Number.isNaN(resultIndex) || resultIndex < 0) {
          // eslint-disable-next-line consistent-return
          return;
        }
        return nestedResultFor(resultIndex);
      },
    });
  }

  moveArg() {
    return this.result;
  }
}

export function toPureArg(txb: Transaction, arg: unknown) {
  if (typeof arg === 'bigint') {
    return txb.pure.u64(arg);
  }
  if (typeof arg === 'number') {
    return txb.pure.u64(arg);
  }
  if (typeof arg === 'boolean') {
    return txb.pure.bool(arg);
  }
  if (typeof arg === 'string') {
    if (/^0x[0-9a-fA-F]+$/.test(arg)) {
      return txb.pure.address(arg);
    }
    return txb.pure.string(arg);
  }
  throw new Error(`Unsupported pure argument type: ${typeof arg}`);
}
